<template>
  <div id="app" :class="themeClass">
    <!-- Sidebar - Shown for authenticated users and guests on practice -->
    <SidebarNavigation
      :class="{ 'sidebar-hidden': !showSidebar }"
      :activeTab="currentRoute"
      :userName="user?.full_name || 'Guest'"
      :isGuest="!isAuthenticated"
      @navigate="handleNavigation"
      @logout="handleLogout"
    />

    <!-- Main Content Area -->
    <div :class="['app-content', { 'with-sidebar': showSidebar }]">
      <router-view />
    </div>
  </div>
</template>

<script>
import { computed, onMounted } from "vue";
import { useRouter, useRoute } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import SidebarNavigation from "@/components/SidebarNavigation.vue";

export default {
  name: "App",
  components: {
    SidebarNavigation,
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const authStore = useAuthStore();

    // Check if user is authenticated
    const isAuthenticated = computed(() => authStore.isAuthenticated);
    const user = computed(() => authStore.user);

    // Routes where sidebar should NOT appear (public pages)
    const publicRoutes = [
      "/",
      "/login",
      "/register",
      "/home",
      "/landing",
      "/about",
      "/privacy",
      "/terms",
    ];

    // Routes where guests CAN see the sidebar
    const guestAllowedRoutes = [
      "/practice",
      "/analytics",
      "/settings",
      "/profile",
    ];

    // Determine if sidebar should be shown
    const showSidebar = computed(() => {
      // Don't show on public routes
      if (publicRoutes.includes(route.path)) return false;

      // Don't show if route explicitly says no sidebar
      if (route.meta?.hideSidebar) return false;

      // Show for authenticated users on any non-public route
      if (isAuthenticated.value) return true;

      // Show for guests on specifically allowed routes (like practice)
      if (guestAllowedRoutes.includes(route.path)) return true;

      return false;
    });

    // Get current route for active tab highlighting
    const currentRoute = computed(() => {
      // Extract route name or first path segment
      if (route.name) return route.name;

      const pathSegments = route.path.split("/").filter(Boolean);
      return pathSegments[0] || "practice";
    });

    const handleNavigation = (tabId) => {
      // Navigation is handled by the SidebarNavigation component
      // This is just for any additional logic you might need
    };

    const handleLogout = () => {
      authStore.logout();
      router.push("/");
    };

    const userTheme = computed(
      () => authStore.settings?.appearance?.theme || "auto",
    );

    const themeClass = computed(() => {
      if (userTheme.value === "light") return "light-theme";
      if (userTheme.value === "dark") return "dark-theme";

      // Auto: detect system preference
      const prefersDark = window.matchMedia(
        "(prefers-color-scheme: dark)",
      ).matches;
      return prefersDark ? "dark-theme" : "light-theme";
    });

    // Watch for system preference changes (for auto mode)
    onMounted(() => {
      const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
      mediaQuery.addEventListener("change", () => {
        // Force re-compute when system preference changes
        if (userTheme.value === "auto") {
          // Vue will auto-update themeClass
        }
      });
    });

    return {
      isAuthenticated,
      user,
      showSidebar,
      currentRoute,
      handleNavigation,
      handleLogout,
      themeClass,
    };
  },
};
</script>

<style>
/* Theme CSS Variables */
.light-theme {
  --bg-primary: #faf8f4;
  --bg-primary-rgb: 250, 248, 244;
  --text-primary: #1a1625;
  --text-primary-rgb: 26, 22, 37;
  --bg-secondary: #f0ebe3;
  --bg-secondary-rgb: 240, 235, 227;
  --text-secondary: #5a4f6a;
  --text-secondary-rgb: 90, 79, 106;
  --accent: #d4a849;
  --accent-rgb: 212, 168, 73;
}

.dark-theme {
  --bg-primary: #13111c;
  --bg-primary-rgb: 19, 17, 28;
  --bg-secondary: rgb(23, 20, 32);
  --bg-secondary-rgb: 23, 20, 32;
  --text-primary: #fff6e3;
  --text-primary-rgb: 255, 246, 227;
  --text-secondary: #fee7b3;
  --text-secondary-rgb: 254, 231, 179;
  --accent: #ffde7a;
  --accent-rgb: 255, 222, 122;
}

/* Global Styles */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
  font-family:
    "Space Grotesk",
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
}

#app {
  font-family:
    "Space Grotesk",
    -apple-system,
    BlinkMacSystemFont,
    "Segoe UI",
    Roboto,
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  min-height: 100vh;
}

/* App Content Area */
.app-content {
  min-height: 100vh;
  transition: margin-left 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* When sidebar is visible, add left margin on desktop */
.app-content.with-sidebar {
  margin-left: 70px;
}

/* Mobile: Remove left margin, add bottom padding for bottom nav */
@media (max-width: 768px) {
  .app-content.with-sidebar {
    margin-left: 0;
    padding-bottom: 70px;
  }
}
</style>
